import { PcodeAds } from '../../../typings/pcode';
import { mapVideoCreativeOptionsValueToApi } from '../../AddCreativePage/mappers';
import {
    CanvasApiBaseResponse,
    CreateVideoAdditionApiResponse,
    CreateVideoAdditionRequestData,
    EMPTY_VIDEO_PREVIEW_DATA,
    FetchPreviewVastRequestData,
    FetchPreviewVastResponse,
    FetchVideoDataResponse,
    VIDEO_ADDITIONS_URL,
    VideoAdditionApiResponse,
    VideoFromCreativeIdApiResponse
} from '../../common/requestsTypings';
import { ResourceType, SharedData } from '../../common/typings';
import { CreativeOptionsData, CreativeOptionsValue } from '../../CreativeOptions/typings';
import { CanvasApiMethodExecutor } from '../CanvasApiMethodExecutor';

const VIDEO_ADDITION_RESOURCE_TYPES = {
    [ResourceType.video]: 'video',
    [ResourceType.audio]: 'audio',
    [ResourceType.image]: 'packshot'
};

export class AddVideoCreativeMethodExecutor extends CanvasApiMethodExecutor {
    async fetchVideoAddition(
        id: string,
        isPreset?: boolean,
        sharedData?: SharedData
    ): Promise<VideoAdditionApiResponse> {
        const path = `${VIDEO_ADDITIONS_URL}/${isPreset ? 'presets' : 'additions'}/${id}`;

        return await this.createRequest<VideoAdditionApiResponse>(path, {}, sharedData);
    }

    async fetchVideoByCreativeId(id?: string, sharedData?: SharedData): Promise<VideoFromCreativeIdApiResponse> {
        const path = `${VIDEO_ADDITIONS_URL}/creatives/${id}/addition`;

        return await this.createRequest<VideoFromCreativeIdApiResponse>(path, {}, sharedData);
    }

    async fetchVideoAdditionFileData(id: string, sharedData?: SharedData): Promise<FetchVideoDataResponse> {
        const path = `${VIDEO_ADDITIONS_URL}/files/${id}`;

        return this.createRequest<FetchVideoDataResponse>(path, {}, sharedData);
    }

    async deleteVideoAdditionFileData(id: string, sharedData?: SharedData): Promise<CanvasApiBaseResponse> {
        const path = `${VIDEO_ADDITIONS_URL}/files/${id}`;
        const params = {
            method: 'DELETE'
        };

        return this.createRequest<CanvasApiBaseResponse>(path, params, sharedData);
    }

    async uploadVideoAdditionFileByLink(
        type: ResourceType,
        url: string,
        sharedData?: SharedData
    ): Promise<FetchVideoDataResponse> {
        const path = `${VIDEO_ADDITIONS_URL}/files/url`;
        const queryParams = [`type=${VIDEO_ADDITION_RESOURCE_TYPES[type]}`];

        const params = {
            method: 'POST',
            body: JSON.stringify({ url })
        };

        return await this.createRequest<FetchVideoDataResponse>(path, params, sharedData, queryParams);
    }

    async uploadVideoAdditionFile(
        type: ResourceType,
        file: File,
        sharedData?: SharedData
    ): Promise<FetchVideoDataResponse> {
        const path = `${VIDEO_ADDITIONS_URL}/files`;
        const queryParams = [`type=${VIDEO_ADDITION_RESOURCE_TYPES[type]}`];

        const formData = new FormData();
        formData.append('file', file, file.name);

        const params = {
            method: 'POST',
            body: formData,
            headers: this.headersWithoutContentType
        };

        return await this.createRequest<FetchVideoDataResponse>(path, params, sharedData, queryParams);
    }

    async fetchPreviewVast(
        optionsValue: CreativeOptionsValue,
        bundle: { name: string },
        presetId: string,
        videoPreviewData?: PcodeAds,
        sharedData?: SharedData
    ): Promise<FetchPreviewVastResponse> {
        const requestData: FetchPreviewVastRequestData = {
            data: {
                bundle,
                elements: mapVideoCreativeOptionsValueToApi(optionsValue)
            },
            preset_id: presetId,
            previewData: {
                ...EMPTY_VIDEO_PREVIEW_DATA,
                ...videoPreviewData
            } as PcodeAds
        };

        const path = `${VIDEO_ADDITIONS_URL}/additions/preview`;
        const params = {
            method: 'POST',
            body: JSON.stringify(requestData)
        };

        return await this.createRequest<FetchPreviewVastResponse>(path, params, sharedData);
    }

    async createVideoAddition(
        creativeOptions: CreativeOptionsData,
        bundle: { name: string },
        presetId: string,
        sharedData?: SharedData
    ): Promise<CreateVideoAdditionApiResponse> {
        const requestData: CreateVideoAdditionRequestData = {
            data: {
                bundle,
                elements: mapVideoCreativeOptionsValueToApi(creativeOptions.optionsValue, true)
            },
            preset_id: presetId,
            name: creativeOptions.name
        };

        const path = `${VIDEO_ADDITIONS_URL}/additions`;
        const params = {
            method: 'POST',
            body: JSON.stringify(requestData)
        };

        return await this.createRequest<CreateVideoAdditionApiResponse>(path, params, sharedData);
    }
}
